"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { formatRemaining, remainingUntil } from "../lib/format";
import type { PublicContestDetail } from "../types";

/**
 * `EX-CPG02`, Wave 30 decision 5 (the canvas's TabBar board): once the dark
 * head's tabs scroll out of view, a compact dark bar under the Judge bar
 * (`top-16`) carries the title, the same tabs as a pill track, the countdown,
 * and Register, which brings the head's door back into view. Hidden it is
 * `inert` and out of the tab order, so a keyboard never meets two copies of
 * the tabs. 56 px and a border, the height the white bar it replaces had, so
 * the standings' sticky header (`stickyTop` 121) lines up under it as before.
 */
export function ContestStickyBar({
  contest,
  locale,
  visible,
  now,
  tabs,
  onRegister,
}: {
  contest: PublicContestDetail;
  locale: string;
  visible: boolean;
  now: number | null;
  tabs: ReactNode;
  onRegister: (() => void) | null;
}) {
  const t = useTranslations("contest.page");
  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;
  const before = contest.phase === "draft" || contest.phase === "scheduled";
  const target = contest.isOpen ? contest.endAt : before ? contest.startAt : null;
  const remaining = now === null ? null : remainingUntil(target, now);

  return (
    <div
      data-testid="contest-sticky-bar"
      aria-hidden={!visible}
      inert={!visible}
      className={cn(
        "fixed inset-x-0 top-16 z-30 border-b border-white/10 bg-neutral-900 text-white shadow-card transition-[opacity,transform] duration-200 ease-out",
        visible ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0",
      )}
    >
      <Container>
        <div className="flex h-14 items-center gap-4">
          <b className="hidden max-w-[260px] truncate text-[15px] lg:block">{title}</b>
          <div className="min-w-0 flex-1">{tabs}</div>
          {remaining && remaining.total > 0 ? (
            <span className="hidden shrink-0 whitespace-nowrap text-[13.5px] text-neutral-300 md:inline">
              {contest.isOpen ? t("endsIn") : t("startsIn")}{" "}
              <b className="font-mono font-bold tabular-nums text-white">{formatRemaining(remaining)}</b>
            </span>
          ) : null}
          {onRegister ? (
            <Button type="button" size="sm" className="shrink-0" onClick={onRegister}>
              {t("stickyRegister")}
            </Button>
          ) : null}
        </div>
      </Container>
    </div>
  );
}
